import axios from "axios";

export const clientPlatform = () => {
  if (typeof window === "undefined") return "web";
  if (window.Capacitor?.isNativePlatform?.()) return window.Capacitor.getPlatform();
  return window.matchMedia?.("(display-mode: standalone)").matches ? "pwa" : "web";
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WORKSPACE_KEY = "workspace_id";
const wsHeader = () => { const w = localStorage.getItem(WORKSPACE_KEY); return w ? { "X-Workspace-Id": w } : {}; };

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Client-Platform"] = clientPlatform();
  Object.assign(config.headers, wsHeader());
  return config;
});

// Server-Sent-Events POST reader (fetch supports auth headers; EventSource does not).
// idleMs: abort if the server sends nothing for that long. Any early end (drop, stall) → onClose, else onError.
export async function streamPost(path, body, { onToken, onDone, onError, onClose, onStatus, onReady, onFrame, idleMs } = {}) {
  const token = localStorage.getItem("token");
  const ctrl = new AbortController();
  let timer;
  const arm = () => { if (!idleMs) return; clearTimeout(timer); timer = setTimeout(() => ctrl.abort(), idleMs); };
  let resp;
  arm();
  try {
    resp = await fetch(`${API}${path}`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Client-Platform": clientPlatform(), ...wsHeader(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    if (ctrl.signal.aborted && onClose) onClose();
    else onError?.(ctrl.signal.aborted ? "The server took too long to respond. Please try again." : "Network error. Please try again.");
    return;
  }
  if (!resp.ok || !resp.body) {
    clearTimeout(timer);
    let detail;
    try { detail = (await resp.json()).detail; } catch (e) {}
    onError?.(detail || `Request failed (${resp.status})`);
    return;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let gotDone = false;
  try {
    while (true) {
      arm();
      const { value, done } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      let idx;
      while ((idx = buf.indexOf("\n\n")) !== -1) {
        const block = buf.slice(0, idx);
        buf = buf.slice(idx + 2);
        const line = block.split("\n").find((l) => l.startsWith("data: "));
        if (!line) continue;
        let data;
        try { data = JSON.parse(line.slice(6)); } catch (e) { continue; }
        onFrame?.(data);
        if (data.type === "token") onToken?.(data.content);
        else if (data.type === "done") { gotDone = true; onDone?.(data); }
        else if (data.type === "status") onStatus?.(data.content, data);
        else if (data.type === "ready") onReady?.(data);
        else if (data.type === "error") { gotDone = true; onError?.(data.message); }
      }
    }
  } catch (e) {
    // Connection dropped or idle-aborted mid-stream; handled below.
  } finally {
    clearTimeout(timer);
  }
  // Stream ended without a `done` frame (proxy timeout, network drop, stalled server).
  if (!gotDone) {
    if (onClose) onClose();
    else onError?.("Connection interrupted. Please try again.");
  }
}

export function apiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default api;
